
import React from 'react';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { SaaSFeatureKey } from '../../types/saas';
import { GlassCard } from '../ui/GlassCard';
import { Lock, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FeatureGuardProps {
    feature: SaaSFeatureKey;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

export const FeatureGuard: React.FC<FeatureGuardProps> = ({ feature, children, fallback }) => {
    const { hasFeature, isPlatformAdmin, loading } = useTenant();
    const { role } = useAuth();

    if (loading) return null; // Or a spinner

    // 1. Platform Admins and Studio Mode (Non-SaaS) bypass checks
    // 2. SUPER_ADMIN user role bypasses checks (God Mode)
    if (isPlatformAdmin || role === 'SUPER_ADMIN') {
        return <>{children}</>;
    }

    // 3. Check Feature based on Plan
    if (hasFeature(feature)) {
        return <>{children}</>;
    }

    // 4. Fallback UI (Upgrade Prompt)
    if (fallback) return <>{fallback}</>;

    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-4 animate-in fade-in zoom-in duration-300">
            <GlassCard className="max-w-md text-center border-orange-500/20 bg-orange-900/5 p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-orange-900/20">
                    <Lock size={32} className="text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-white uppercase tracking-widest mb-2">Feature Locked</h2>
                <p className="text-neutral-400 text-sm mb-8 leading-relaxed">
                    The <strong>{feature.replace('_', ' ').toUpperCase()}</strong> module is not included in your current plan. Upgrade to unlock this feature.
                </p>

                <div className="flex flex-col gap-3">
                    <Link 
                        to="/dashboard/studio" 
                        className="w-full py-3 bg-white text-black font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-neutral-200 transition-all flex items-center justify-center gap-2"
                    >
                        <ShieldCheck size={14} /> View Plans & Upgrade
                    </Link>
                    <Link 
                        to="/dashboard" 
                        className="w-full py-3 border border-white/10 text-white font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-white/5 transition-all"
                    >
                        Back to Dashboard
                    </Link>
                </div>
            </GlassCard>
        </div>
    );
};
